import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User, Building2, ShieldCheck, Zap } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, register } = useAuth();
  const params = new URLSearchParams(location.search);
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickAccts, setQuickAccts] = useState([]);

  useEffect(() => { api.get("/auth/quick-accounts").then((r) => setQuickAccts(r.data)).catch(() => {}); }, []);
  const QICONS = { user: User, building: Building2, shield: ShieldCheck };

  const quickLogin = async (acc) => {
    setBusy(true);
    try {
      const u = await login(acc.email, acc.password);
      toast.success(`Connecté en tant que ${acc.label}`);
      navigate(u?.business ? "/business-app" : "/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user) navigate(user.business ? "/business-app" : "/app"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "register" ? await register(email, password, name) : await login(email, password);
      toast.success("Bienvenue sur OMY 👋");
      navigate(u?.business ? "/business-app" : "/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden">
      <div className="aurora" style={{ width: 420, height: 420, background: "#E05A36", top: -100, left: -60, opacity: 0.3 }} />
      <button onClick={() => navigate("/")} data-testid="auth-back-btn"
        className="absolute top-6 left-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Accueil
      </button>

      <div className="relative z-10 w-full max-w-md omy-glass rounded-3xl p-8">
        <Logo className="mb-6" />
        <h1 className="font-display text-2xl font-semibold">
          {mode === "register" ? "Créez votre mémoire OMY" : "Ravi de vous revoir"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          {mode === "register" ? "Gratuit, sans carte bancaire." : "Connectez-vous pour retrouver votre mémoire."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name">Prénom</Label>
              <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                required className="mt-1.5 bg-black/30" placeholder="Jeremy" />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="auth-email-input" value={email}
              onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-black/30" placeholder="vous@email.com" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" type="password" data-testid="auth-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-black/30" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="w-full bg-primary hover:bg-primary/90 rounded-full h-11">
            {busy ? "…" : mode === "register" ? "Créer mon compte" : "Se connecter"}
          </Button>
        </form>

        <div className="mt-6" data-testid="quick-login-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-muted-foreground flex items-center gap-1"><Zap size={12} className="text-primary" /> Accès rapide démo</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {quickAccts.map((acc) => {
              const Icon = QICONS[acc.icon] || User;
              return (
                <button key={acc.email} data-testid={`quick-login-${acc.icon}`} onClick={() => quickLogin(acc)} disabled={busy}
                  title={acc.description}
                  className="omy-glass rounded-xl p-3 flex flex-col items-center gap-1.5 hover:border-primary/50 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                  <Icon size={18} className="text-primary" />
                  <span className="text-xs font-medium">{acc.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-muted-foreground text-center mt-6">
          {mode === "register" ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
          <button data-testid="auth-toggle-btn" onClick={() => setMode(mode === "register" ? "login" : "register")}
            className="text-primary hover:underline">
            {mode === "register" ? "Se connecter" : "S'inscrire"}
          </button>
        </p>
      </div>
    </div>
  );
}
